import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Send, Bot, User } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useWindowWidth } from '../hooks/useWindowWidth';

interface Message {
  sender: 'user' | 'bot';
  text: string;
}

const Chat: React.FC = () => {
  const [data, setData] = useState<{ content: string }[]>([]);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [sending, setSending] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const isMobile = useWindowWidth() < 768;

  useEffect(() => {
    fetch('/data.json')
      .then(r => r.json())
      .then(setData)
      .catch(err => console.error('Error loading data:', err));
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMsg: Message = { sender: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    const dataContent = data.map(d => d.content).join('\n');
    const systemPrompt = `You are an AI assistant for Elola Technologies. Answer questions helpfully based on the following context about the company. If a question isn't covered by the context, answer using your general knowledge.\n\n${dataContent}`;

    try {
      const response = await axios.post(
        '/api/chat',
        { message: input, systemPrompt },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const botText = response.data.text;
      setMessages(prev => [...prev, { sender: 'bot', text: botText }]);
    } catch (err: any) {
      let errorText = "I'm having trouble responding. Please try again later.";
      if (err.response?.status === 400) {
        errorText = 'Invalid request. Please try a different question.';
      } else if (err.response?.status === 429) {
        errorText = 'Rate limit reached. Please wait a moment and try again.';
      } else if (err.response) {
        errorText = `Error: ${err.response.data?.error || 'Unknown error'}`;
      }
      setMessages(prev => [...prev, { sender: 'bot', text: errorText }]);
    }

    setSending(false);
  };

  return (
    <>
      <Helmet>
        <title>Chat with Elola Tech AI | AI, Web Development & Custom Software</title>
        <meta name="description" content="Have a question about AI, web development, or Elola Technologies? Chat live with Elola Tech AI, built by Marcus S. Elola to assist with all things digital." />
        <link rel="canonical" href="https://elola.tech/chat" />
        <meta property="og:title" content="Chat with Elola Tech AI | AI, Web Development & Custom Software" />
        <meta property="og:description" content="Elola Tech AI is here to answer your questions about web development, AI tools, custom software, and more." />
        <meta property="og:image" content="https://elola.tech/preview-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div style={{
        minHeight: '100vh',
        paddingTop: 'var(--navbar-height)',
        background: 'var(--bg-base)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          flex: 1,
          maxWidth: '800px',
          width: '100%',
          margin: '0 auto',
          padding: isMobile ? '24px 16px' : '40px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center' }}
          >
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-purple-dark) 100%)',
              marginBottom: '16px',
              boxShadow: '0 0 24px rgba(109, 74, 255, 0.4)',
            }}>
              <Bot size={28} color="white" />
            </div>
            <h1 style={{ fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 700, marginBottom: '8px' }}>
              Elola Tech AI
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
              Ask anything about web development, AI, blockchain, or Elola Technologies.
            </p>
          </motion.div>

          {/* Chat Window */}
          <div
            ref={chatRef}
            style={{
              flex: 1,
              minHeight: isMobile ? '50vh' : '55vh',
              maxHeight: '60vh',
              overflowY: 'auto',
              background: 'var(--bg-card)',
              border: '1px solid var(--border-card)',
              borderRadius: '16px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {messages.length === 0 ? (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                color: 'var(--text-muted)',
                textAlign: 'center',
                padding: '24px',
              }}>
                <Bot size={40} opacity={0.3} />
                <p style={{ fontSize: '15px', fontStyle: 'italic', maxWidth: '320px' }}>
                  Feel free to ask me anything about Elola Technologies, or any other topic you'd like to discuss.
                </p>
              </div>
            ) : (
              messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    display: 'flex',
                    justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    gap: '10px',
                    alignItems: 'flex-start',
                  }}
                >
                  {msg.sender === 'bot' && (
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'rgba(109, 74, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}>
                      <Bot size={16} color="var(--accent-purple-light)" />
                    </div>
                  )}
                  <div style={{
                    maxWidth: '75%',
                    padding: '12px 16px',
                    borderRadius: msg.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: msg.sender === 'user'
                      ? 'linear-gradient(135deg, var(--accent-purple), var(--accent-purple-dark))'
                      : 'rgba(255,255,255,0.05)',
                    border: msg.sender === 'bot' ? '1px solid var(--border-card)' : 'none',
                    fontSize: '15px',
                    lineHeight: '1.6',
                    color: 'var(--text-primary)',
                  }}>
                    {msg.sender === 'bot' ? (
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p style={{ margin: '0 0 8px', lineHeight: '1.65' }}>{children}</p>,
                          strong: ({ children }) => <strong style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{children}</strong>,
                          ul: ({ children }) => <ul style={{ margin: '6px 0 8px', paddingLeft: '18px' }}>{children}</ul>,
                          ol: ({ children }) => <ol style={{ margin: '6px 0 8px', paddingLeft: '18px' }}>{children}</ol>,
                          li: ({ children }) => <li style={{ marginBottom: '4px', lineHeight: '1.6' }}>{children}</li>,
                          h3: ({ children }) => <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '10px 0 4px' }}>{children}</h3>,
                          h4: ({ children }) => <h4 style={{ fontSize: '14px', fontWeight: 700, margin: '8px 0 4px' }}>{children}</h4>,
                        }}
                      >
                        {msg.text}
                      </ReactMarkdown>
                    ) : (
                      msg.text
                    )}
                  </div>
                  {msg.sender === 'user' && (
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'rgba(255,255,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}>
                      <User size={16} color="var(--text-secondary)" />
                    </div>
                  )}
                </motion.div>
              ))
            )}
            {sending && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'rgba(109, 74, 255, 0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Bot size={16} color="var(--accent-purple-light)" />
                </div>
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '16px 16px 16px 4px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--border-card)',
                  display: 'flex',
                  gap: '6px',
                  alignItems: 'center',
                }}>
                  {[0, 1, 2].map(d => (
                    <div key={d} style={{
                      width: '7px', height: '7px', borderRadius: '50%',
                      background: 'var(--accent-purple-light)',
                      opacity: 0.6,
                      animation: `bob 1.2s ${d * 0.2}s ease-in-out infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={messages.length === 0 ? 'Start the conversation...' : 'Type your message...'}
              disabled={sending}
              style={{
                flex: 1,
                padding: '14px 20px',
                fontSize: '16px',
                borderRadius: '50px',
                border: '1px solid var(--border-card)',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.currentTarget.style.borderColor = 'var(--accent-purple)')}
              onBlur={e => (e.currentTarget.style.borderColor = 'var(--border-card)')}
            />
            <motion.button
              type="submit"
              disabled={!input.trim() || sending}
              whileHover={!input.trim() || sending ? {} : { scale: 1.05 }}
              whileTap={!input.trim() || sending ? {} : { scale: 0.95 }}
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--accent-purple), var(--accent-purple-dark))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                opacity: !input.trim() || sending ? 0.5 : 1,
                cursor: !input.trim() || sending ? 'not-allowed' : 'pointer',
                boxShadow: '0 0 20px rgba(109, 74, 255, 0.3)',
              }}
            >
              <Send size={18} color="white" />
            </motion.button>
          </form>
        </div>
      </div>
    </>
  );
};

export default Chat;
